import { createAsyncThunk } from '@reduxjs/toolkit';
import {
    CreatePettyCashFundRequest,
    GetPettyCashFundRequests,
    ApprovePettyCashFundRequest,
    RejectPettyCashFundRequest,
    GetPettyCashActiveFund,
    ClosePettyCashFund,
    GetPettyCashFundHistory,
    CreatePettyCashExpense,
    GetPettyCashExpenses,
    GetPettyCashVoucherTypes,
    CreatePettyCashVoucherType,
    DeletePettyCashVoucherType,
    GetPettyCashVendors,
    CreatePettyCashVendor,
    DeletePettyCashVendor,
    GetPettyCashBranches,
    CreatePettyCashBranch,
    DeletePettyCashBranch,
    GetPettyCashExpenseTypes,
    CreatePettyCashExpenseType,
    DeletePettyCashExpenseType,
    GetPettyCashBudgetCodes,
    CreatePettyCashBudgetCode,
    DeletePettyCashBudgetCode,
    GetPettyCashReports,
    GetPettyCashEmployees
} from 'helpers/api_method';

// Fund Request Thunks
export const createPettyCashFundRequest = createAsyncThunk(
    'pettyCash/createFundRequest',
    async (data: any) => {
        const response: any = await CreatePettyCashFundRequest(data);
        if (response && response.status) {
            return response.data;
        } else {
            throw new Error(response?.message || "Failed to create fund request");
        }
    }
);

export const getPettyCashFundRequests = createAsyncThunk(
    'pettyCash/getFundRequests',
    async (filters?: any) => {
        const response: any = await GetPettyCashFundRequests(filters);
        
        if (response && (response.status === true || response.success === true)) {
            return response.data;
        } else {
            throw new Error(response?.message || "Failed to get fund requests");
        }
    }
);

export const approvePettyCashFundRequest = createAsyncThunk(
    'pettyCash/approveFundRequest',
    async ({ request_id, data }: { request_id: string, data: any }) => {
        const response: any = await ApprovePettyCashFundRequest(request_id, data);
        if (response && response.status) {
            return response.data;
        } else {
            throw new Error(response?.message || "Failed to approve fund request");
        }
    }
);

export const rejectPettyCashFundRequest = createAsyncThunk(
    'pettyCash/rejectFundRequest',
    async ({ request_id, data }: { request_id: string, data: any }) => {
        const response: any = await RejectPettyCashFundRequest(request_id, data);
        if (response && response.status) {
            return response.data;
        } else {
            throw new Error(response?.message || "Failed to reject fund request");
        }
    }
);

// Fund Management Thunks
export const getPettyCashActiveFund = createAsyncThunk(
    'pettyCash/getActiveFund',
    async () => {
        const response: any = await GetPettyCashActiveFund();
        if (response && response.status) {
            return response.data;
        } else {
            throw new Error(response?.message || "Failed to get active fund");
        }
    }
);

export const closePettyCashFund = createAsyncThunk(
    'pettyCash/closeFund',
    async ({ fund_id, data }: { fund_id: string, data: any }) => {
        const response: any = await ClosePettyCashFund(fund_id, data);
        if (response && (response.success === true || response.status === true)) {
            return response.data;
        } else {
            throw new Error(response?.message || "Failed to close fund");
        }
    }
);

export const getPettyCashFundHistory = createAsyncThunk(
    'pettyCash/getFundHistory',
    async () => {
        const response: any = await GetPettyCashFundHistory();
        if (response && (response.success === true || response.status === true)) {
            return response.data;
        } else {
            throw new Error(response?.message || "Failed to get fund history");
        }
    }
);

// Expense Management Thunks
export const createPettyCashExpense = createAsyncThunk(
    'pettyCash/createExpense',
    async (data: any, { rejectWithValue }) => {
        try {
            const response: any = await CreatePettyCashExpense(data);
            if (response && (response.success === true || response.status === true)) {
                return response.data;
            } else {
                return rejectWithValue(response?.message || "Failed to create expense");
            }
        } catch (error: any) {
            // Handle 400 errors and other network errors
            return rejectWithValue(error.message || "Failed to create expense");
        }
    }
);

export const getPettyCashExpenses = createAsyncThunk(
    'pettyCash/getExpenses',
    async (filters?: any) => {
        const response: any = await GetPettyCashExpenses(filters);
        if (response && (response.success === true || response.status === true)) {
            return response.data;
        } else {
            throw new Error(response?.message || "Failed to get expenses");
        }
    }
);

// Catalog Management Thunks
export const getPettyCashVoucherTypes = createAsyncThunk(
    'pettyCash/getVoucherTypes',
    async () => {
        const response: any = await GetPettyCashVoucherTypes();
        if (response && response.status) {
            return response.data;
        } else {
            throw new Error(response?.message || "Failed to get voucher types");
        }
    }
);

export const createPettyCashVoucherType = createAsyncThunk(
    'pettyCash/createVoucherType',
    async (data: any) => {
        const response: any = await CreatePettyCashVoucherType(data);
        if (response && response.status) {
            return response.data;
        } else {
            throw new Error(response?.message || "Failed to create voucher type");
        }
    }
);

export const deletePettyCashVoucherType = createAsyncThunk(
    'pettyCash/deleteVoucherType',
    async (id: string) => {
        const response: any = await DeletePettyCashVoucherType(id);
        if (response && response.status) {
            return id; // Return the deleted ID for state update
        } else {
            throw new Error(response?.message || "Failed to delete voucher type");
        }
    }
);

export const getPettyCashVendors = createAsyncThunk(
    'pettyCash/getVendors',
    async () => {
        const response: any = await GetPettyCashVendors();
        if (response && response.status) {
            return response.data;
        } else {
            throw new Error(response?.message || "Failed to get vendors");
        }
    }
);

export const createPettyCashVendor = createAsyncThunk(
    'pettyCash/createVendor',
    async (data: any) => {
        const response: any = await CreatePettyCashVendor(data);
        if (response && response.status) {
            return response.data;
        } else {
            throw new Error(response?.message || "Failed to create vendor");
        }
    }
);

export const deletePettyCashVendor = createAsyncThunk(
    'pettyCash/deleteVendor',
    async (id: string) => {
        const response: any = await DeletePettyCashVendor(id);
        if (response && response.status) {
            return id; // Return the deleted ID for state update
        } else {
            throw new Error(response?.message || "Failed to delete vendor");
        }
    }
);

export const getPettyCashBranches = createAsyncThunk(
    'pettyCash/getBranches',
    async () => {
        const response: any = await GetPettyCashBranches();
        if (response && response.status) {
            return response.data;
        } else {
            throw new Error(response?.message || "Failed to get branches");
        }
    }
);

export const createPettyCashBranch = createAsyncThunk(
    'pettyCash/createBranch',
    async (data: any) => {
        const response: any = await CreatePettyCashBranch(data);
        if (response && response.status) {
            return response.data;
        } else {
            throw new Error(response?.message || "Failed to create branch");
        }
    }
);

export const deletePettyCashBranch = createAsyncThunk(
    'pettyCash/deleteBranch',
    async (id: string) => {
        const response: any = await DeletePettyCashBranch(id);
        if (response && response.status) {
            return id; // Return the deleted ID for state update
        } else {
            throw new Error(response?.message || "Failed to delete branch");
        }
    }
);

export const getPettyCashExpenseTypes = createAsyncThunk(
    'pettyCash/getExpenseTypes',
    async () => {
        const response: any = await GetPettyCashExpenseTypes();
        if (response && response.status) {
            return response.data;
        } else {
            throw new Error(response?.message || "Failed to get expense types");
        }
    }
);

export const createPettyCashExpenseType = createAsyncThunk(
    'pettyCash/createExpenseType',
    async (data: any) => {
        const response: any = await CreatePettyCashExpenseType(data);
        if (response && response.status) {
            return response.data;
        } else {
            throw new Error(response?.message || "Failed to create expense type");
        }
    }
);

export const deletePettyCashExpenseType = createAsyncThunk(
    'pettyCash/deleteExpenseType',
    async (id: string) => {
        const response: any = await DeletePettyCashExpenseType(id);
        if (response && response.status) {
            return id; // Return the deleted ID for state update
        } else {
            throw new Error(response?.message || "Failed to delete expense type");
        }
    }
);

export const getPettyCashBudgetCodes = createAsyncThunk(
    'pettyCash/getBudgetCodes',
    async () => {
        const response: any = await GetPettyCashBudgetCodes();
        if (response && response.status) {
            return response.data;
        } else {
            throw new Error(response?.message || "Failed to get budget codes");
        }
    }
);

export const createPettyCashBudgetCode = createAsyncThunk(
    'pettyCash/createBudgetCode',
    async (data: any) => {
        const response: any = await CreatePettyCashBudgetCode(data);
        if (response && response.status) {
            return response.data;
        } else {
            throw new Error(response?.message || "Failed to create budget code");
        }
    }
);

export const deletePettyCashBudgetCode = createAsyncThunk(
    'pettyCash/deleteBudgetCode',
    async (id: string) => {
        const response: any = await DeletePettyCashBudgetCode(id);
        if (response && response.status) {
            return id; // Return the deleted ID for state update
        } else {
            throw new Error(response?.message || "Failed to delete budget code");
        }
    }
);

// Reporting Thunks
export const getPettyCashReports = createAsyncThunk(
    'pettyCash/getReports',
    async (filters?: any) => {
        const response: any = await GetPettyCashReports(filters);
        if (response && (response.success === true || response.status === true)) {
            return response.data;
        } else {
            throw new Error(response?.message || "Failed to get reports");
        }
    }
);

// Employee Management Thunks
export const getPettyCashEmployees = createAsyncThunk(
    'pettyCash/getEmployees',
    async (_, { rejectWithValue }) => {
        try {
            const response: any = await GetPettyCashEmployees();
            
            if (response && response.status) {
                return response.data;
            } else {
                return rejectWithValue(response?.message || "Failed to get employees");
            }
        } catch (error: any) {
            return rejectWithValue(error.message || "Failed to get employees");
        }
    }
);
